import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './datePickerStyles.css';

// -------------------------------
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from 'lucide-react';
import moment, { Moment } from 'moment-timezone';
import { useState, useEffect } from 'react';
import { CalendarDayShape, DateRangePicker, FocusedInputShape } from 'react-dates';
import { useFormContext } from 'react-hook-form';
import useResponsive from '../../../../hooks/useResponsive';
import DatePickerDay from './DatePickerDay';
import { Button } from '../../../../components/ui/Button';
import { brandConfig } from '../../../../config';
import { useTheme } from '../../../../theme/ThemeProvider';

const minDate = moment().subtract(1, 'days');
const maxDate = moment().add(2, 'years');

export interface DatePickerProps {
  blockedDates: { check_in: Date; check_out: Date; sfID: String }[];
  startDateName: string;
  endDateName: string;
  existingReservation?: { check_in: Date; check_out: Date; sfID: String };
  isEditingMode?: boolean;
  disablePortal?: boolean;
}

moment.tz.setDefault(brandConfig.timezone);

export function ReservationDatePicker({
  blockedDates,
  startDateName,
  endDateName,
  existingReservation,
  isEditingMode = false,
  disablePortal = false
}: DatePickerProps) {
  const { watch, setError, setValue, clearErrors } = useFormContext();
  const startDate = watch(startDateName);
  const endDate = watch(endDateName);

  const isExistingReservationWithPastCheckIn =
    existingReservation && moment(existingReservation.check_in).isSameOrBefore(moment(), 'day');

  const { resolvedMode } = useTheme();
  const [focusedInput, setFocusInput] = useState<FocusedInputShape | null>(null);
  const [hasSkippedFirstClick, setHasSkippedFirstClick] = useState(false);

  useEffect(() => {
    if (isEditingMode) {
      setHasSkippedFirstClick(false);
    }
  }, [isEditingMode]);

  useEffect(() => {
    if (focusedInput !== null && isEditingMode) {
      setHasSkippedFirstClick(false);
    }
  }, [focusedInput, isEditingMode]);

  // Set CSS variables for the date picker so colors respect light/dark theme
  useEffect(() => {
    document.documentElement.style.setProperty('--picker-background', 'hsl(var(--background))');
    document.documentElement.style.setProperty('--picker-primary', 'hsl(var(--primary))');
    document.documentElement.style.setProperty(
      '--picker-primary-foreground',
      'hsl(var(--primary-foreground))'
    );
    document.documentElement.style.setProperty('--picker-secondary', 'hsl(var(--secondary))');
    document.documentElement.style.setProperty(
      '--picker-secondary-foreground',
      'hsl(var(--secondary-foreground))'
    );
    document.documentElement.style.setProperty('--picker-color', 'hsl(var(--foreground))');
    document.documentElement.style.setProperty('--picker-outline', 'hsl(var(--border))');
    document.documentElement.style.setProperty('--picker-muted', 'hsl(var(--muted-foreground))');
  }, [resolvedMode]);

  const isFullscreen = useResponsive('down', 'sm');

  const numberOfMonths = isFullscreen ? 3 : 2;
  const orientation = !isFullscreen ? 'horizontal' : 'vertical';

  const startDateForDateRangePicker = startDate ? moment(startDate) : null;
  const endDateForDateRangePicker = endDate ? moment(endDate) : null;

  const existingReservationsCheckIns = blockedDates.map((date) => date.check_in);
  const existingReservationsCheckOuts = blockedDates.map((date) => date.check_out);

  const selectionComplete = startDate !== null && endDate !== null;
  const selectActive = startDate !== null && endDate === null;

  const checkSelectionInBlockedDates = (start: Moment | null, end: Moment | null) => {
    if (start === null && end === null) {
      return false;
    }

    for (let i = 0; i < blockedDates.length; i++) {
      const { check_in, check_out } = blockedDates[i];
      if (start) {
        if (start.isBetween(check_in, check_out)) {
          return true;
        }
        if (start.isSame(check_in, 'day')) {
          return true;
        }
      }
      if (end) {
        if (end.isBetween(check_in, check_out)) {
          return true;
        }
        if (end.isSame(check_out, 'day')) {
          return true;
        }
      }
      if (start && end) {
        if (start.isBefore(check_in) && end.isAfter(check_out)) {
          return true;
        }
      }
    }
    return false;
  };

  const setStartDate = (a: Moment | null) => {
    const value = a ? a.toDate() : null;
    setValue(startDateName, value);
  };
  const setEndDate = (a: Moment | null) => {
    const value = a ? a.toDate() : null;
    setValue(endDateName, value);
  };

  return (
    <DateRangePicker
      readOnly
      keepFocusOnInput={false}
      isOutsideRange={(day) => {
        const isBeforeToday = day.isBefore(new Date(), 'day');

        if (isEditingMode && isExistingReservationWithPastCheckIn && existingReservation) {
          const originalCheckIn = moment(existingReservation.check_in);
          if (day.isSame(originalCheckIn, 'day')) {
            return false;
          }
        }

        if (isBeforeToday) {
          return true;
        }
        return false;
      }}
      isDayBlocked={(day) => {
        let isBlockedCheckin = false;
        let isBlockedCheckOut = false;
        if (day && existingReservationsCheckIns.find((compDate) => day.isSame(compDate, 'day'))) {
          isBlockedCheckin = true;
        }
        if (day && existingReservationsCheckOuts.find((compDate) => day.isSame(compDate, 'day'))) {
          isBlockedCheckOut = true;
        }
        if (isBlockedCheckin && isBlockedCheckOut) {
          return true;
        }

        const blockingReservation = blockedDates.find((reservationBounds) => {
          return day.isBetween(reservationBounds.check_in, reservationBounds.check_out);
        });
        return !!blockingReservation;
      }}
      small={isFullscreen}
      withPortal={!disablePortal || isFullscreen}
      withFullScreenPortal={isFullscreen}
      numberOfMonths={numberOfMonths}
      hideKeyboardShortcutsPanel
      noBorder
      verticalSpacing={0}
      horizontalMargin={0}
      firstDayOfWeek={0}
      minDate={minDate}
      maxDate={maxDate}
      showClearDates={!isEditingMode}
      showDefaultInputIcon
      startDatePlaceholderText="Check In"
      startDate={startDateForDateRangePicker}
      startDateAriaLabel="Check In"
      startDateId={startDateName}
      endDatePlaceholderText="Check Out"
      endDateAriaLabel="Check Out"
      endDate={endDateForDateRangePicker}
      endDateId={endDateName}
      onDatesChange={({ startDate: inputStartDate, endDate: inputEndDate }) => {
        clearErrors();
        const isBlocked = checkSelectionInBlockedDates(inputStartDate, inputEndDate);
        if (isBlocked) {
          setValue(startDateName, null);
          setValue(endDateName, null);
          setFocusInput('startDate');
          setError(endDateName, {
            message: 'Selection blocked by another booking. Please try again.'
          });
          return;
        }
        setStartDate(inputStartDate ? inputStartDate : null);
        setEndDate(inputEndDate ? inputEndDate : null);
      }}
      focusedInput={focusedInput}
      onFocusChange={(x) => {
        if (
          isEditingMode &&
          isExistingReservationWithPastCheckIn &&
          focusedInput === null &&
          x === 'startDate' &&
          !hasSkippedFirstClick
        ) {
          setHasSkippedFirstClick(true);
          setFocusInput('endDate');
          return;
        }

        if (x === null && focusedInput !== null) {
          setHasSkippedFirstClick(false);
          if (isEditingMode && existingReservation) {
            setStartDate(moment(existingReservation.check_in));
            setEndDate(moment(existingReservation.check_out));
          }
        }

        setFocusInput(x);
      }}
      orientation={orientation}
      screenReaderInputMessage="Select a check in and out date."
      renderNavPrevButton={(props) => {
        const { ariaLabel, ...buttonProps } = props;
        const useInlinePositioning = disablePortal && !isFullscreen;
        if (orientation === 'horizontal') {
          return (
            <Button
              size="icon"
              {...buttonProps}
              aria-label={ariaLabel}
              className={useInlinePositioning ? 'absolute top-5 left-5' : 'fixed left-[10%]'}
              variant="outline"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          );
        }
        return (
          <Button
            size="lg"
            {...buttonProps}
            aria-label={ariaLabel}
            className="fixed bottom-0 left-0 w-1/2 rounded-none z-[100000] disabled:bg-muted disabled:text-muted-foreground"
            variant="outline"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        );
      }}
      renderNavNextButton={(props) => {
        const { ariaLabel, ...buttonProps } = props;
        const useInlinePositioning = disablePortal && !isFullscreen;
        if (orientation === 'horizontal') {
          return (
            <Button
              size="icon"
              {...buttonProps}
              aria-label={ariaLabel}
              className={useInlinePositioning ? 'absolute top-5 right-5' : 'fixed right-[10%]'}
              variant="outline"
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          );
        }
        return (
          <Button
            size="lg"
            {...buttonProps}
            aria-label={ariaLabel}
            className="fixed bottom-0 right-0 w-1/2 rounded-none z-[100000]"
            variant="outline"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        );
      }}
      renderCalendarDay={({ day, ...props }) => {
        let isBlockedCheckin = false;
        let isBlockedCheckOut = false;
        if (day) {
          if (existingReservationsCheckIns.find((compDate) => day.isSame(compDate, 'day'))) {
            isBlockedCheckin = true;
          }
          if (existingReservationsCheckOuts.find((compDate) => day.isSame(compDate, 'day'))) {
            isBlockedCheckOut = true;
          }
        }

        if (isEditingMode && isExistingReservationWithPastCheckIn && existingReservation) {
          const originalCheckIn = moment(existingReservation.check_in);
          const isOriginalCheckInDate = day && day.isSame(originalCheckIn, 'day');

          if (
            isOriginalCheckInDate &&
            focusedInput === 'startDate' &&
            isExistingReservationWithPastCheckIn
          ) {
            props.onDayClick = (_day, event) => {
              event.preventDefault();
              event.stopPropagation();
            };
          }

          if (focusedInput === 'startDate') {
            const originalOnDayClick = props.onDayClick;
            props.onDayClick = (clickedDay, event) => {
              if (clickedDay && existingReservation) {
                const existingCheckIn = moment(existingReservation.check_in);
                const today = moment().startOf('day');
                if (
                  clickedDay.isAfter(existingCheckIn, 'day') &&
                  clickedDay.isSameOrAfter(today, 'day')
                ) {
                  const isBlocked = checkSelectionInBlockedDates(existingCheckIn, clickedDay);
                  if (isBlocked) {
                    clearErrors();
                    setError(endDateName, {
                      message: 'Selection blocked by another booking. Please try again.'
                    });
                    return;
                  }
                  clearErrors();
                  setEndDate(clickedDay);
                  setFocusInput(null);
                  return;
                }
              }
              if (originalOnDayClick) {
                originalOnDayClick(clickedDay, event);
              }
            };
          }

          if (focusedInput === 'endDate') {
            const originalOnDayClick = props.onDayClick;
            props.onDayClick = (clickedDay, event) => {
              if (clickedDay && existingReservation) {
                const existingCheckIn = moment(existingReservation.check_in);
                const today = moment().startOf('day');
                if (
                  clickedDay.isAfter(existingCheckIn, 'day') &&
                  clickedDay.isSameOrAfter(today, 'day')
                ) {
                  const isBlocked = checkSelectionInBlockedDates(existingCheckIn, clickedDay);
                  if (isBlocked) {
                    clearErrors();
                    setError(endDateName, {
                      message: 'Selection blocked by another booking. Please try again.'
                    });
                    return;
                  }
                  clearErrors();
                  setEndDate(clickedDay);
                  setFocusInput(null);
                  return;
                }
              }
              if (originalOnDayClick) {
                originalOnDayClick(clickedDay, event);
              }
            };
          }
        }

        let manuallyCalculatedIsSelectedSpan = false;
        if (
          isExistingReservationWithPastCheckIn &&
          existingReservation &&
          day &&
          endDateForDateRangePicker
        ) {
          const originalCheckIn = moment(existingReservation.check_in);

          const dayStart = day.clone().startOf('day');
          const checkInStart = originalCheckIn.clone().startOf('day');
          const endDateStart = endDateForDateRangePicker.clone().startOf('day');

          manuallyCalculatedIsSelectedSpan = dayStart.isBetween(
            checkInStart,
            endDateStart,
            'day',
            '[]'
          );
        }

        if (focusedInput === 'startDate' && isBlockedCheckin) {
          props.onDayClick = (_day, event) => {
            event.preventDefault();
            event.stopPropagation();
          };
        }
        if (focusedInput === 'endDate' && isBlockedCheckOut) {
          props.onDayClick = (_day, event) => {
            event.preventDefault();
            event.stopPropagation();
          };
        }
        return (
          <CalendarDay
            isBlockedCheckin={isBlockedCheckin}
            isBlockedCheckOut={isBlockedCheckOut}
            isLockedCheckIn={
              !!(
                isExistingReservationWithPastCheckIn &&
                existingReservation &&
                day &&
                day.isSame(moment(existingReservation.check_in), 'day')
              )
            }
            selectActive={selectActive}
            selectionComplete={selectionComplete}
            manuallyCalculatedIsSelectedSpan={manuallyCalculatedIsSelectedSpan}
            day={day}
            {...props}
          />
        );
      }}
      weekDayFormat="ddd"
      renderWeekHeaderElement={(week) => (
        <span className="block w-10 truncate text-center text-[10px] text-foreground">{week}</span>
      )}
      renderDayContents={(day, modifiers) => {
        const textColor = 'inherit';
        return (
          <div className="flex items-center justify-center">
            <span
              className="text-center text-sm"
              style={{
                color: textColor,
                fontWeight: modifiers.has('today') ? 'bold' : 'normal'
              }}
            >
              {day.format('D')}
            </span>
          </div>
        );
      }}
    />
  );
}

interface CalendarDayI extends CalendarDayShape {
  isBlockedCheckin: boolean;
  isBlockedCheckOut: boolean;
  isLockedCheckIn: boolean;
  selectActive: boolean;
  selectionComplete: boolean;
  manuallyCalculatedIsSelectedSpan?: boolean;
}

function CalendarDay({
  day,
  modifiers,
  tabIndex,
  onDayClick,
  onDayMouseEnter,
  onDayMouseLeave,
  renderDayContents,
  isBlockedCheckin,
  isBlockedCheckOut,
  isLockedCheckIn,
  selectActive,
  selectionComplete,
  manuallyCalculatedIsSelectedSpan
}: CalendarDayI) {
  if (!day || !modifiers) {
    return <DatePickerDay isValid={false} />;
  }

  const onClick = onDayClick
    ? (event: React.MouseEvent) => {
        onDayClick(day, event as React.MouseEvent<HTMLTableDataCellElement>);
      }
    : undefined;

  const onMouseEnter = onDayMouseEnter
    ? (event: React.MouseEvent) => {
        onDayMouseEnter(day, event as React.MouseEvent<HTMLTableDataCellElement>);
      }
    : undefined;

  const onMouseLeave = onDayMouseLeave
    ? (event: React.MouseEvent) => {
        onDayMouseLeave(day, event as React.MouseEvent<HTMLTableDataCellElement>);
      }
    : undefined;

  const isToday = modifiers.has('today');
  const isValid = modifiers.has('valid');
  const isFirstDayOfWeek = modifiers.has('first-day-of-week');
  const isLastDayOfWeek = modifiers.has('last-day-of-week');
  const isBlocked = modifiers.has('blocked');
  const isBlockedCalendar = modifiers.has('blocked-calendar');
  const isBlockedOutOfRange = modifiers.has('blocked-out-of-range');
  const isBlockedByMinNights = modifiers.has('blocked-minimum-nights');
  const isSelectedStart = modifiers.has('selected-start');
  const noSelectedEnd = modifiers.has('selected-start-no-selected-end');
  const isSelectedEnd = modifiers.has('selected-end');
  const isSelectedSpan =
    modifiers.has('selected-span') || manuallyCalculatedIsSelectedSpan || false;
  const isHovered = modifiers.has('hovered');
  const isHoveredSpan = modifiers.has('hovered-span');
  const isHighlighted = modifiers.has('highlighted-calendar');

  const Content = renderDayContents ? renderDayContents(day, modifiers) : null;

  return (
    <DatePickerDay
      day={day}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      tabIndex={tabIndex}
      isToday={isToday}
      isValid={isValid}
      isFirstDayOfWeek={isFirstDayOfWeek}
      isLastDayOfWeek={isLastDayOfWeek}
      isBlocked={isBlocked}
      isBlockedCalendar={isBlockedCalendar}
      isBlockedOutOfRange={isBlockedOutOfRange}
      isBlockedByMinNights={isBlockedByMinNights}
      isSelectedStart={isSelectedStart}
      noSelectedEnd={noSelectedEnd}
      isSelectedEnd={isSelectedEnd}
      isSelectedSpan={isSelectedSpan}
      isHovered={isHovered}
      isHoveredSpan={isHoveredSpan}
      isFocused={false}
      isBlockedCheckIn={isBlockedCheckin}
      isBlockedCheckOut={isBlockedCheckOut}
      selectActive={selectActive}
      selectionComplete={selectionComplete}
      isHighlighted={isHighlighted}
      isLockedCheckIn={isLockedCheckIn}
    >
      {Content}
    </DatePickerDay>
  );
}
